import { UnfinishedTestCaseExecutionsTableRowProps } from '@qality/common-components';
import { IS_CLOUD } from '@qality/common-config';
import { Pagination as IPagination, User } from '@qality/common-model';
import {
  TestExecutionReportService,
  UserPropertyService,
} from '@qality/common-service';

import { Property } from './UserPropertyService';

const UNFINISHED_TEST_CASE_EXECUTIONS_GADGET_USER_PROPERTY_KEY =
  'soldevelo-qality-dashboard-unfinished-test-executions-gadget';

type UnfinishedTestExecutionsGadgetProperty = {
  showAssignedToMeOnly: boolean;
};

type UnfinishedExecutionFilters = {
  accountId?: string;
  projectId?: string;
  statusCategories?: string;
};

type UnfinishedTestCaseExecutionsTableData = {
  pagination: IPagination;
  data: Array<UnfinishedTestCaseExecutionsTableRowProps>;
};

const getUnfinishedExecutionsFilters = (
  showAssignedToMeOnly: boolean,
  projectId: string,
  user: User
): UnfinishedExecutionFilters => {
  const unfinishedExecutionsFilters: UnfinishedExecutionFilters = {
    statusCategories: 'UNFINISHED',
  };

  if (showAssignedToMeOnly) {
    unfinishedExecutionsFilters['accountId'] = user.getIdentifier();
  }

  if (projectId) {
    unfinishedExecutionsFilters['projectId'] = projectId;
  }

  if (IS_CLOUD) {
    unfinishedExecutionsFilters['migrateMissingTestCases'] = false;
  }

  return unfinishedExecutionsFilters;
};

export default {
  async getUserProperty(
    user: User
  ): Promise<UnfinishedTestExecutionsGadgetProperty> {
    let userProperty: UnfinishedTestExecutionsGadgetProperty;
    try {
      const response: Property = await UserPropertyService.getProperty(
        UNFINISHED_TEST_CASE_EXECUTIONS_GADGET_USER_PROPERTY_KEY,
        user
      );
      userProperty = response.value;
    } catch (e) {
      userProperty = { showAssignedToMeOnly: true };
    }
    return userProperty;
  },

  setUserProperty(
    user: User,
    property: UnfinishedTestExecutionsGadgetProperty
  ) {
    UserPropertyService.setProperty(
      UNFINISHED_TEST_CASE_EXECUTIONS_GADGET_USER_PROPERTY_KEY,
      property,
      user
    );
  },

  async getUnfinishedTestCaseExecutionsData(
    user: User,
    showAssignedToMeOnly: boolean,
    projectId: string,
    pageNumber: number,
    pageSize: number
  ): Promise<UnfinishedTestCaseExecutionsTableData> {
    const unfinishedExecutionsFilters = getUnfinishedExecutionsFilters(
      showAssignedToMeOnly,
      projectId,
      user
    );
    const result = await TestExecutionReportService.getData(
      pageNumber,
      unfinishedExecutionsFilters,
      pageSize
    );

    return {
      pagination: {
        ...result.pagination,
        last:
          result.pagination.currentPage === result.pagination.totalPages - 1,
      },
      data: this.mapToUnfinishedTestCaseExecutionsData(result.data),
    };
  },

  mapToUnfinishedTestCaseExecutionsData(
    data: any
  ): Array<UnfinishedTestCaseExecutionsTableRowProps> {
    return data.rows.content.map((row: any) => {
      const columnMap: { [key: string]: any } = {};

      data.columnKeys.forEach((column: any, index: number) => {
        columnMap[column.key] = row[index];
      });

      const testCase = columnMap['test_case'];
      const testCycle = columnMap['test_cycle'];
      const createdOn = columnMap['created_on'];
      const assignee = columnMap['execution_assignee'];
      const testResult = columnMap['test_result'];
      const testExecutionUrl = columnMap['actions'];

      return {
        id: testCase?.id,
        status: {
          id: testResult?.id,
          name: testResult?.name,
          isDefault: testResult?.isDefault,
          position: testResult?.position,
          color: testResult?.color,
          category: testResult?.category,
        },
        createdDate: createdOn,
        assignee: {
          accountId: assignee?.accountId,
          displayName: assignee?.displayName,
          avatarUrls: assignee?.avatarUrl
            ? {
                '16x16': assignee?.avatarUrl,
                '24x24': assignee?.avatarUrl,
                '32x32': assignee?.avatarUrl,
                '48x48': assignee?.avatarUrl,
              }
            : {},
          getDisplayName: () => assignee?.displayName,
          getAvatar: () => assignee?.avatarUrl,
          getIdentifier: () => assignee?.accountId,
        },
        testCase: {
          issueKey: testCase?.key,
          title: testCase?.summary,
        },
        testCycle: {
          id: testCycle?.id,
          name: testCycle?.name,
        },
        testTypeProps: {
          testType: testCase?.testType,
          isLoading: false,
          isReadOnly: true,
          isIconOnly: true,
          style: { paddingRight: '6px', paddingTop: '6px' },
        },
        testExecutionUrl: testExecutionUrl,
      };
    });
  },
};
